import React, { useState, useMemo } from "react";
import { toast } from "react-toastify";
import { useNavigate, Link } from "react-router-dom";
import { HiChevronDown, HiSearch } from "react-icons/hi";
import { countries } from "../data/countries";
import { sendOtpWhatsApp } from "../utils/whatsappOtp";

const Signup = () => {
  const [form, setForm] = useState({ full_name: "", phone: "" });
  const [countryCode, setCountryCode] = useState("+252");
  const [loading, setLoading] = useState(false);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [countrySearch, setCountrySearch] = useState("");
  const navigate = useNavigate();

  const filteredCountries = useMemo(() => {
    if (!countrySearch.trim()) return countries;
    const q = countrySearch.trim().toLowerCase();
    return countries.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        (c.dial_code && c.dial_code.replace("+", "").includes(q.replace("+", "")))
    );
  }, [countrySearch]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const generateVerificationCode = () => {
    return Math.floor(100000 + Math.random() * 900000).toString();
  };

  const sendVerificationCode = async (rawPhone, code, selectedCode = "+252") =>
    sendOtpWhatsApp({
      phoneNumber: rawPhone,
      countryCode: selectedCode,
      code,
      userType: "User",
    });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const rawPhone = form.phone.replace(/\D/g, "");
      const fullPhone = countryCode + rawPhone;
      const verificationCode = generateVerificationCode();

      await sendVerificationCode(rawPhone, verificationCode, countryCode);

      navigate("/verify", {
        state: {
          phone: fullPhone,
          countryCode,
          full_name: form.full_name,
          userType: "customer",
          userTypeLabel: "User's Account",
          code: verificationCode,
          isNewUser: true,
          existingUserData: null,
        },
      });

      toast.success("Verification code sent to your WhatsApp");
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Failed to send verification code. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const selectedCountry = countries.find((c) => c.dial_code === countryCode) || countries.find((c) => c.dial_code === "+252");

  return (
    <div className="min-h-screen pt-24 pb-16 flex items-center justify-center px-4 sm:px-6 bg-gradient-to-br from-slate-50 via-blue-50/30 to-white">
      <div className="w-full max-w-lg">
        <div className="bg-white rounded-3xl p-8 sm:p-12 shadow-xl shadow-blue-100/50 border border-blue-100/80 relative overflow-hidden">
          {/* Decorative */}
          <div className="absolute -top-16 -right-16 w-48 h-48 bg-gradient-to-br from-[#6CA9F5]/20 to-transparent rounded-full blur-2xl" />
          <div className="absolute -bottom-12 -left-12 w-32 h-32 bg-blue-50 rounded-full opacity-60" />

          <div className="relative">
            <div className="mb-10 text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-50 to-blue-100/80 rounded-2xl flex items-center justify-center mx-auto mb-5 border border-blue-100/80">
                <svg
                  className="w-10 h-10 text-[#6CA9F5]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                </svg>
              </div>
              <h1 className="text-3xl sm:text-4xl font-black text-gray-900 tracking-tight">
                Get Started
              </h1>
              <p className="text-gray-500 mt-2 text-base font-medium">
                Join our healthcare community today
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-3">
                <label className="block text-sm font-bold text-gray-700">
                  Full Name
                </label>
                <input
                  type="text"
                  name="full_name"
                  value={form.full_name}
                  onChange={handleChange}
                  required
                  className="w-full px-5 py-4 bg-gradient-to-br from-gray-50 to-gray-100/80 border-2 border-gray-200/80 rounded-2xl focus:bg-white focus:ring-4 focus:ring-[#6CA9F5]/20 focus:border-[#6CA9F5] outline-none transition-all font-semibold text-gray-800 text-base placeholder-gray-400"
                  placeholder="John Doe"
                />
              </div>

              <div className="space-y-3">
                <label className="block text-sm font-bold text-gray-700">
                  Phone Number
                </label>
                <div className="flex gap-3 flex-col sm:flex-row">
                  {/* Country Code Selector */}
                  <div className="relative flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => {
                        setShowCountryDropdown(!showCountryDropdown);
                        setCountrySearch("");
                      }}
                      className="flex items-center justify-between gap-3 w-full sm:w-auto min-w-[160px] px-4 py-4 bg-gradient-to-br from-gray-50 to-gray-100/80 hover:from-blue-50 hover:to-blue-100/50 border-2 border-gray-200/80 hover:border-[#6CA9F5]/50 rounded-2xl focus:ring-4 focus:ring-[#6CA9F5]/20 focus:border-[#6CA9F5] outline-none transition-all duration-200 text-left"
                    >
                      <span className="flex items-center gap-3 flex-1 min-w-0">
                        <span className="text-2xl flex-shrink-0 leading-none" aria-hidden="true">
                          {selectedCountry?.flag || "🇸🇴"}
                        </span>
                        <span className="text-base font-bold text-gray-800 truncate" title={selectedCountry?.dial_code || "+252"}>
                          {selectedCountry?.dial_code || "+252"}
                        </span>
                      </span>
                      <HiChevronDown className={`w-5 h-5 text-gray-500 flex-shrink-0 transition-transform duration-200 ${showCountryDropdown ? "rotate-180" : ""}`} />
                    </button>

                    {showCountryDropdown && (
                      <>
                        <div
                          className="fixed inset-0 z-20"
                          onClick={() => setShowCountryDropdown(false)}
                          aria-hidden="true"
                        />
                        <div className="absolute top-full left-0 mt-2 bg-white rounded-2xl shadow-2xl border-2 border-blue-100 z-30 max-h-80 overflow-hidden w-full min-w-[320px] sm:min-w-[340px] flex flex-col ring-4 ring-blue-50">
                          <div className="p-3 border-b border-gray-100 bg-gray-50/80 sticky top-0">
                            <div className="relative">
                              <HiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                              <input
                                type="text"
                                value={countrySearch}
                                onChange={(e) => setCountrySearch(e.target.value)}
                                placeholder="Search country or code..."
                                className="w-full pl-10 pr-4 py-3 text-base border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#6CA9F5] focus:border-[#6CA9F5] outline-none bg-white"
                              />
                            </div>
                          </div>
                          <div className="overflow-y-auto max-h-64">
                            {filteredCountries.length === 0 ? (
                              <p className="px-4 py-8 text-base text-gray-500 text-center">No countries found</p>
                            ) : (
                              filteredCountries.map((country) => (
                                <button
                                  key={country.code}
                                  type="button"
                                  onClick={() => {
                                    setCountryCode(country.dial_code);
                                    setShowCountryDropdown(false);
                                    setCountrySearch("");
                                  }}
                                  className={`w-full flex items-center gap-3 px-4 py-3.5 hover:bg-blue-50 transition-colors text-left border-b border-gray-50 last:border-0 ${
                                    countryCode === country.dial_code ? "bg-blue-50/80" : ""
                                  }`}
                                >
                                  <span className="text-2xl flex-shrink-0">{country.flag || "🏳"}</span>
                                  <span className="flex-1 text-base font-semibold text-gray-800 truncate">
                                    {country.name}
                                  </span>
                                  <span className="text-base font-bold text-[#6CA9F5] flex-shrink-0">{country.dial_code}</span>
                                </button>
                              ))
                            )}
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Phone Input */}
                  <div className="relative flex-1 min-w-0">
                    <span className="absolute inset-y-0 left-0 pl-4 flex items-center text-gray-400 pointer-events-none">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                    </span>
                    <input
                      type="tel"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      required
                      className="w-full pl-12 pr-4 py-4 bg-gradient-to-br from-gray-50 to-gray-100/80 border-2 border-gray-200/80 rounded-2xl focus:bg-white focus:ring-4 focus:ring-[#6CA9F5]/20 focus:border-[#6CA9F5] outline-none transition-all font-semibold text-gray-800 text-base placeholder-gray-400"
                      placeholder="61 123 4567"
                    />
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-[#6CA9F5] to-blue-500 hover:from-blue-600 hover:to-blue-600 text-white py-4 rounded-2xl font-bold text-lg shadow-lg shadow-blue-300/40 transition-all hover:-translate-y-0.5 active:scale-[0.99] disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  "Create Account"
                )}
              </button>
            </form>

            <div className="mt-10 pt-8 border-t border-gray-100 flex flex-col items-center">
              <p className="text-base text-gray-500 font-medium">
                Already a member?{" "}
                <Link
                  to="/login"
                  className="text-[#6CA9F5] font-bold hover:text-blue-600 transition-colors underline underline-offset-2"
                >
                  Sign In
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;